import { useEffect, useState } from 'react';
import { Bell, Send, Zap, CalendarClock, BellOff } from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import type { ApiResponse } from '../types';
import PageHeader from '../components/ui/PageHeader';
import { SkeletonCard } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/Toast';

type Route = 'instant' | 'digest' | 'off';

interface Settings {
  slack_enabled: boolean;
  slack_webhook_set: boolean;
  digest_enabled: boolean;
  digest_hour_utc: number;
  routes: Record<string, Route>;
  kinds: { kind: string; label: string; severity: 'urgent' | 'normal'; default_route: Route }[];
}

const ROUTE_OPTIONS: { value: Route; label: string; icon: typeof Zap }[] = [
  { value: 'instant', label: 'Instant', icon: Zap },
  { value: 'digest', label: 'Digest', icon: CalendarClock },
  { value: 'off', label: 'Off', icon: BellOff },
];

/** "07:00 UTC (09:00 your time)" */
function hourLabel(h: number): string {
  const d = new Date(Date.UTC(2026, 0, 1, h));
  const local = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `${String(h).padStart(2, '0')}:00 UTC (${local} your time)`;
}

/** Super-admin page: Slack webhook, instant/digest routing per event type, digest hour. */
export default function NotificationSettingsPage() {
  const { fetchApi } = useApi();
  const toast = useToast();
  const [s, setS] = useState<Settings | null>(null);
  const [webhook, setWebhook] = useState('');
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState<'test' | 'digest' | null>(null);

  useEffect(() => {
    fetchApi<ApiResponse<Settings>>('/notifications/settings')
      .then((r) => r.data && setS(r.data))
      .catch((e) => toast.error('Could not load notification settings', e instanceof Error ? e.message : undefined));
  }, [fetchApi, toast]);

  const save = async (patch: Partial<Settings> & { slack_webhook?: string | null }) => {
    setSaving(true);
    try {
      const r = await fetchApi<ApiResponse<Settings>>('/notifications/settings', { method: 'PUT', body: JSON.stringify(patch) });
      if (r.data) setS((prev) => (prev ? { ...prev, ...r.data } : prev));
      return true;
    } catch (e) {
      toast.error('Could not save settings', e instanceof Error ? e.message : undefined);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveWebhook = async () => {
    if (await save({ slack_webhook: webhook.trim() || null })) {
      setWebhook('');
      toast.success(webhook.trim() ? 'Slack webhook saved' : 'Slack webhook removed');
    }
  };

  const runAction = async (kind: 'test' | 'digest') => {
    setBusy(kind);
    try {
      const r = await fetchApi<ApiResponse<{ sent: boolean; events?: number; reason?: string }>>(`/notifications/${kind}`, { method: 'POST' });
      if (kind === 'test') toast.success('Test message sent', 'Check the Slack channel.');
      else if (r.data?.sent) toast.success('Digest sent', `${r.data.events} item${r.data.events === 1 ? '' : 's'}.`);
      else toast.info('No digest sent', r.data?.reason);
    } catch (e) {
      toast.error(kind === 'test' ? 'Test message failed' : 'Digest failed', e instanceof Error ? e.message : undefined);
    } finally {
      setBusy(null);
    }
  };

  if (!s) return <div className="space-y-6"><PageHeader title="Notifications" /><SkeletonCard lines={5} /><SkeletonCard lines={8} /></div>;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notifications"
        description="Where DomainHQ sends alerts. Everyone sees the in-app bell (filtered to what they can access); Slack delivery is configured here."
      />

      <div className="card p-6 space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Slack</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              Posts to one channel through an <a className="text-brand-600 hover:underline" href="https://api.slack.com/messaging/webhooks" target="_blank" rel="noopener noreferrer">incoming webhook</a>.
              {s.slack_webhook_set ? ' A webhook is saved.' : ' No webhook saved yet.'}
            </p>
          </div>
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-700">
            <input type="checkbox" checked={s.slack_enabled} disabled={saving || !s.slack_webhook_set}
              onChange={(e) => save({ slack_enabled: e.target.checked })}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
            Send to Slack
          </label>
        </div>

        <div>
          <label className="label" htmlFor="webhook">{s.slack_webhook_set ? 'Replace webhook URL' : 'Webhook URL'}</label>
          <div className="flex flex-wrap gap-2">
            <input id="webhook" type="password" autoComplete="off" className="input flex-1 min-w-[16rem]" value={webhook}
              onChange={(e) => setWebhook(e.target.value)} placeholder={s.slack_webhook_set ? '•••••••• (saved — paste a new one to replace)' : 'https://hooks.slack.com/services/…'} />
            <button className="btn btn-primary" disabled={saving || !webhook.trim()} onClick={saveWebhook}>Save</button>
            {s.slack_webhook_set && (
              <button className="btn btn-secondary" disabled={saving} onClick={async () => { if (await save({ slack_webhook: null, slack_enabled: false })) toast.success('Slack webhook removed'); }}>Remove</button>
            )}
          </div>
          <p className="mt-1.5 text-xs text-slate-500">Stored encrypted and never shown again. Only super admins can change these settings.</p>
        </div>

        <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
          <button className="btn btn-secondary" disabled={!s.slack_webhook_set || busy !== null} onClick={() => runAction('test')}>
            <Send className="mr-2 h-4 w-4" />{busy === 'test' ? 'Sending…' : 'Send test message'}
          </button>
          <button className="btn btn-secondary" disabled={!s.slack_webhook_set || !s.slack_enabled || busy !== null} onClick={() => runAction('digest')}>
            <CalendarClock className="mr-2 h-4 w-4" />{busy === 'digest' ? 'Sending…' : 'Send digest now'}
          </button>
        </div>
      </div>

      <div className="card p-6 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Daily digest</h2>
            <p className="mt-0.5 text-sm text-slate-500">One Slack message a day with everything new, including a recap of instant alerts.</p>
          </div>
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-700">
            <input type="checkbox" checked={s.digest_enabled} disabled={saving} onChange={(e) => save({ digest_enabled: e.target.checked })}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500" />
            Enabled
          </label>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm text-slate-600" htmlFor="hour">Send at</label>
          <select id="hour" className="input w-auto" value={s.digest_hour_utc} disabled={saving || !s.digest_enabled}
            onChange={(e) => save({ digest_hour_utc: Number(e.target.value) })}>
            {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
          </select>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="border-b border-slate-200 px-6 py-4">
          <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900"><Bell className="h-4 w-4 text-slate-400" />What goes to Slack</h2>
          <p className="mt-0.5 text-sm text-slate-500"><strong>Instant</strong> posts as soon as it's detected (checked every 15 minutes); <strong>Digest</strong> waits for the daily summary; <strong>Off</strong> keeps it in the bell only.</p>
        </div>
        <ul className="divide-y divide-slate-100">
          {s.kinds.map((k) => {
            const current = s.routes[k.kind] ?? k.default_route;
            return (
              <li key={k.kind} className="flex flex-wrap items-center justify-between gap-3 px-6 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900">{k.label}</p>
                  <p className="text-xs text-slate-500">{k.severity === 'urgent' ? 'Urgent' : 'Routine'} · default: {k.default_route}</p>
                </div>
                <div role="radiogroup" aria-label={`${k.label} delivery`} className="flex rounded-lg bg-slate-100 p-0.5">
                  {ROUTE_OPTIONS.map((o) => {
                    const on = current === o.value;
                    return (
                      <button key={o.value} role="radio" aria-checked={on} disabled={saving}
                        onClick={() => !on && save({ routes: { [k.kind]: o.value } as Record<string, Route> })}
                        className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${on ? 'bg-surface text-brand-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                        <o.icon className="h-3.5 w-3.5" />{o.label}
                      </button>
                    );
                  })}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
